import React, { useEffect, useRef, useState } from "react";

interface CollapsibleProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  onToggle?: (isOpen: boolean) => void;
  className?: string;
  contentClassName?: string;
  triggerClassName?: string;
}

const ANIMATION_DURATION = 300;

export function Collapsible({
  trigger,
  children,
  open = false,
  onToggle,
  className = "",
  contentClassName = "",
  triggerClassName = "",
}: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState(open);
  const [height, setHeight] = useState<number | undefined>(
    open ? undefined : 0,
  );
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (!contentRef.current) return;

    if (isOpen) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);

      // After animation completes, set to undefined to allow for content resizing
      const timer = setTimeout(() => {
        setHeight(undefined);
      }, ANIMATION_DURATION); // Match this with the CSS transition time

      return () => clearTimeout(timer);
    } else {
      // Set the current height before collapsing
      setHeight(contentRef.current.scrollHeight);

      // Force a reflow
      contentRef.current.getBoundingClientRect();

      // Set to zero to trigger the animation
      setTimeout(() => {
        setHeight(0);
      }, 0);
    }
  }, [isOpen]);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };

  return (
    <div className={className}>
      <div
        className={triggerClassName}
        onClick={handleToggle}
        aria-expanded={isOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
        style={{ cursor: "pointer" }}
      >
        {trigger}
      </div>
      <div
        className={`collapsible-content ${contentClassName}`}
        ref={contentRef}
        style={{
          height: height === undefined ? "auto" : `${height}px`,
          overflow: "auto",
          transition: `height ${ANIMATION_DURATION}ms ease-in-out`,
        }}
        aria-hidden={!isOpen}
      >
        <div>{children}</div>
      </div>
    </div>
  );
}
